// PickingsListXL - Sprint 44 Fase 3 Vista 7.
// Lista de pickings XL. Filtros: estado XLChip pills + busqueda debounced.
// XLTable + pager. Tokens + dark.
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ensureKeyframes, injectCSS } from '../../design/motion';
import usePickingsXL from './hooks/usePickingsXL';
import XLTable from '../../components/xl/XLTable';
import XLInput from '../../components/xl/XLInput';
import XLBadge from '../../components/xl/XLBadge';
import XLChip from '../../components/xl/XLChip';

const CSS_ID = 'xl-pick-list-css';
const CSS = `
.xl-page { padding: 24px; font-family: var(--xl-font-sans); color: var(--xl-color-text) }
.xl-page__head {
  display: flex; align-items: flex-start; justify-content: space-between;
  gap: 16px; margin-bottom: 16px; flex-wrap: wrap;
}
.xl-page__title { font-size: 22px; font-weight: 600; margin: 0 }
.xl-page__subtitle { font-size: 13px; color: var(--xl-color-text-secondary); margin: 2px 0 0 }

.xl-filters {
  background: var(--xl-color-surface); border: 1px solid var(--xl-color-border);
  border-radius: var(--xl-radius-lg); padding: 16px; margin-bottom: 16px;
  box-shadow: var(--xl-shadow-sm);
  display: flex; flex-direction: column; gap: 12px;
}
.xl-pills { display: flex; flex-wrap: wrap; gap: 6px }

.xl-banner {
  padding: 10px 14px; margin-bottom: 12px;
  border-radius: var(--xl-radius-md); font-size: 13px;
  background: rgba(220,38,38,0.10); color: var(--xl-color-danger);
  border: 1px solid rgba(220,38,38,0.28);
}

.xl-pager {
  display: flex; align-items: center; justify-content: space-between;
  gap: 12px; padding: 12px 4px; margin-top: 12px;
  color: var(--xl-color-text-secondary); font-size: 13px;
}
.xl-pager__group { display: flex; gap: 8px; align-items: center }
.xl-mono { font-family: var(--xl-font-mono); font-size: 12px }
.xl-btn {
  font: 500 13px/1 var(--xl-font-sans); padding: 10px 16px;
  border-radius: var(--xl-radius-md); border: 1px solid var(--xl-color-border);
  background: transparent; color: var(--xl-color-text-secondary); cursor: pointer;
}
.xl-btn:hover { background: var(--xl-color-surface); color: var(--xl-color-text) }
.xl-btn:focus-visible { outline: 2px solid var(--xl-color-focus); outline-offset: 2px }
.xl-btn:disabled { opacity: 0.45; cursor: not-allowed }
`;

const ESTADO_VARIANT = {
  pendiente:  'neutral',
  en_proceso: 'info',
  completado: 'success',
};
const ESTADO_LABEL = {
  pendiente:  'Pendiente',
  en_proceso: 'En proceso',
  completado: 'Completado',
};
const ESTADO_FILTROS = [
  { value: '',           label: 'Todos' },
  { value: 'pendiente',  label: 'Pendiente' },
  { value: 'en_proceso', label: 'En proceso' },
  { value: 'completado', label: 'Completado' },
];

function useDebouncedSetter(setter, delay) {
  const [local, setLocal] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setter(local), delay);
    return () => clearTimeout(t);
  }, [local, delay, setter]);
  return [local, setLocal];
}

export default function PickingsListXL() {
  useEffect(() => { ensureKeyframes(); injectCSS(CSS_ID, CSS); }, []);
  const navigate = useNavigate();
  const px = usePickingsXL();
  const [searchLocal, setSearchLocal] = useDebouncedSetter(px.setQ, 300);

  const columns = useMemo(() => ([
    { key: 'id', label: '#',
      render: (p) => <span className="xl-mono">#{p.id}</span> },
    { key: 'venta', label: 'Venta',
      render: (p) => <span className="xl-mono">{(p.venta && p.venta.numero) || '\u2014'}</span> },
    { key: 'cliente', label: 'Cliente',
      render: (p) => (p.venta && p.venta.cliente_nombre) || '\u2014' },
    { key: 'fecha', label: 'Fecha',
      render: (p) => p.fecha || '\u2014' },
    { key: 'estado', label: 'Estado',
      render: (p) => (
        <XLBadge variant={ESTADO_VARIANT[p.estado] || 'neutral'} dot>
          {ESTADO_LABEL[p.estado] || p.estado}
        </XLBadge>
      ) },
  ]), []);

  const { page, totalPages, total, canPrev, canNext, loading } = px;

  return (
    <div className="xl-page">
      <header className="xl-page__head">
        <div>
          <h2 className="xl-page__title">Picking</h2>
          <p className="xl-page__subtitle">Preparacion de pedidos con escaner.</p>
        </div>
      </header>

      <section className="xl-filters" aria-label="Filtros de picking">
        <div className="xl-pills" role="tablist" aria-label="Estado">
          {ESTADO_FILTROS.map((f) => (
            <XLChip key={f.value || 'all'}
              onClick={() => px.setEstado(f.value)}
              selected={px.estado === f.value}>
              {f.label}
            </XLChip>
          ))}
        </div>
        <XLInput
          type="search"
          label="Buscar"
          placeholder="Numero de venta..."
          value={searchLocal}
          onChange={(e) => setSearchLocal(e.target.value)}
        />
      </section>

      {px.err && <div className="xl-banner" role="alert">{px.err}</div>}

      <XLTable
        ariaLabel="Lista de pickings"
        columns={columns}
        data={px.items}
        loading={loading}
        onRowClick={(p) => navigate('/picking/' + p.id)}
        rowKey="id"
        emptyMessage="Sin pickings para estos filtros."
      />

      <div className="xl-pager">
        <span>
          {total
            ? ('Pagina ' + page + ' de ' + totalPages + ' \u00b7 ' + total + ' resultados')
            : 'Sin resultados'}
        </span>
        <span className="xl-pager__group">
          <button type="button" className="xl-btn"
            disabled={!canPrev || loading}
            onClick={() => px.setPage(page - 1)}>Anterior</button>
          <span className="xl-mono">{page} / {totalPages}</span>
          <button type="button" className="xl-btn"
            disabled={!canNext || loading}
            onClick={() => px.setPage(page + 1)}>Siguiente</button>
        </span>
      </div>
    </div>
  );
}
